import React, { useEffect, useState } from "react";
import Wrapper from "../../../components/Wrapper";
import { useTheme as useAppTheme } from "../../../context/ThemeContext";
import { themeBg } from "../../../config/themes";
import "./dashboard.css";
import title from "../../../components/title";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GetProperty } from "../../../redux/reducer/GetPropertyDetails";
import { GetAllTenant } from "../../../redux/reducer/GetAllTenant";
import { GetLandlordInterestRequests } from "../../../redux/reducer/FlatInterestRequest";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Skeleton,
  Avatar,
  Chip,
  LinearProgress,
  IconButton,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  Home as HomeIcon,
  People as PeopleIcon,
  AccountBalance as MoneyIcon,

  Add as AddIcon,

  Notifications as NotificationIcon,
  CheckCircle as CheckIcon,

  Schedule as PendingIcon,
  ArrowForward as ArrowIcon,

  Build as MaintenanceIcon,

  RocketLaunch as RocketIcon,
  Apartment as ApartmentIcon,
  VerifiedUser as VerifiedIcon,
  Handshake as HandshakeIcon,
  LocationOn as LocationIcon,
  Bed as BedIcon,
  Bathtub as BathIcon,
  SquareFoot as AreaIcon,
  Edit as EditIcon,
  AccessTime as TimeIcon,
  HourglassEmpty as WaitingIcon,
} from "@mui/icons-material";
export default function Dashboard() {
  title("Dashboard");
  const { theme: appTheme } = useAppTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const open = useSelector((state) => state.sidebar.open);
  const { validateUser } = useSelector((state) => state);
  const userId = validateUser?.UserValidate?.data?.user?._id;

  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [interestRequests, setInterestRequests] = useState([]);

  // Fetch all dashboard data
  useEffect(() => {
    if (userId) {
      setLoading(true);
      Promise.all([
        dispatch(GetProperty({ userId, from: "landlord" })),
        dispatch(GetAllTenant({ userId })),
        dispatch(GetLandlordInterestRequests({ landlordId: userId })),
      ]).then(([propRes, tenantRes, requestRes]) => {
        setProperties(propRes?.payload?.data?.data || []);
        setTenants(tenantRes?.payload?.data?.data || []);
        setInterestRequests(requestRes?.payload?.data?.data || []);
        setLoading(false);
      });
    }
  }, [dispatch, userId]);

  // Ensure arrays are valid
  const safeProperties = Array.isArray(properties) ? properties : [];
  const safeTenants = Array.isArray(tenants) ? tenants : [];
  const safeRequests = Array.isArray(interestRequests) ? interestRequests : [];

  // Bookings where the tenant has reported moving in and the owner has not
  // confirmed it yet. Nothing unlocks for that tenant until they do.
  const awaitingConfirmation = safeRequests.filter(
    (r) => r.moveInStatus === "awaiting" && r.tenantMarkedMoveInAt
  );

  // Calculate stats 
  const stats = {
    totalProperties: safeProperties.length,
    availableProperties: safeProperties.filter(
      (p) => Number(p.is_rent) === 0 && p.is_approved
    ).length,
    rentedProperties: safeProperties.filter((p) => Number(p.is_rent) === 1)
      .length,
    pendingApproval: safeProperties.filter(
      (p) => Number(p.is_rent) === 0 && !p.is_approved
    ).length,
    totalTenants: safeTenants.length,
    activeTenants: safeTenants.filter((t) => t.is_accept).length,
    pendingRequests: safeRequests.filter((r) => r.status === "pending").length,
    monthlyEarnings: safeProperties
      .filter((p) => Number(p.is_rent) === 1)
      .reduce((sum, p) => sum + (Number(p.flatRent) || 0), 0),
    expectedEarnings: safeProperties.reduce(
      (sum, p) => sum + (Number(p.flatRent) || 0),
      0
    ),
    pendingPayments: safeTenants.filter((t) => t.is_accept && !t.is_paid)
      .length,
  };

  const occupancyRate =
    stats.totalProperties > 0
      ? Math.round((stats.rentedProperties / stats.totalProperties) * 100)
      : 0;

  // Stat Card Component
  const StatCard = ({ title, value, subtitle, icon, color, onClick }) => (
    <Card
      sx={{
        height: "100%",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.3s ease",
        "&:hover": onClick
          ? {
              transform: "translateY(-4px)",
              borderColor: `${appTheme.primary}55`,
              boxShadow: "0 18px 34px -22px rgba(18,32,58,0.55)",
            }
          : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: `${color}.light`,
              color: `${color}.main`,
              width: 56,
              height: 56,
            }}
          >
            {icon}
          </Avatar>
          {onClick && (
            <IconButton size="small">
              <ArrowIcon />
            </IconButton>
          )}
        </Box>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {value}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.disabled">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Quick Action Button
  const QuickAction = ({ icon, label, onClick, color = "primary" }) => (
    <Button
      variant="outlined"
      startIcon={icon}
      onClick={onClick}
      color={color}
      fullWidth
      sx={{
        py: 1.5,
        justifyContent: "flex-start",
        textTransform: "none",
        borderRadius: 2,
      }}
    >
      {label}
    </Button>
  );

  // Check if user has no tenants (show onboarding)
  const hasNoTenants = safeTenants.length === 0;

  // Onboarding steps configuration
  const onboardingSteps = [
    {
      step: 1,
      title: "Add Your Property",
      description:
        "List your property with photos, details, and rental price to attract potential tenants.",
      icon: <ApartmentIcon sx={{ fontSize: 40 }} />,
      color: "primary",
      completed: safeProperties.length > 0,
      action: () => navigate("/my-properties"),
      actionLabel: "Add Property",
    },
    {
      step: 2,
      title: "Wait for Approval",
      description:
        "Our admin team will review and approve your property listing within 24 hours.",
      icon: <VerifiedIcon sx={{ fontSize: 40 }} />,
      color: "warning",
      completed: safeProperties.some((p) => p.is_approved),
      action: null,
      actionLabel: null,
    },
    {
      step: 3,
      title: "Receive Tenant Requests",
      description:
        "Once approved, tenants can send interest requests for your property.",
      icon: <NotificationIcon sx={{ fontSize: 40 }} />,
      color: "info",
      completed: safeRequests.length > 0,
      action: () => navigate("/new-request"),
      actionLabel: "View Requests",
    },
    {
      step: 4,
      title: "Accept & Create Agreement",
      description:
        "Accept a tenant request and create a rental agreement to get started.",
      icon: <HandshakeIcon sx={{ fontSize: 40 }} />,
      color: "success",
      completed: safeTenants.length > 0,
      action: null,
      actionLabel: null,
    },
  ];

  // Get current step (first incomplete step)
  const currentStep =
    onboardingSteps.findIndex((s) => !s.completed) + 1 ||
    onboardingSteps.length;

  // Onboarding UI Component
  const OnboardingView = () => (
    <Box sx={{ py: 2 }}>
      {/* Welcome Header */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          background: themeBg(
            appTheme,
            `linear-gradient(130deg, ${appTheme.navBg} 0%, ${appTheme.primary} 100%)`
          ),
          color: "white",
          borderRadius: 3,
          textAlign: "center",
        }}
      >
        <RocketIcon sx={{ fontSize: 60, mb: 2, opacity: 0.9 }} />
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Welcome to OTS,{" "}
          {validateUser?.UserValidate?.data?.user?.ownerName || "Landlord"}!
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
          Let's get you started with managing your properties
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.8 }}>
          Follow these simple steps to list your property and find tenants
        </Typography>
      </Paper>

      {/* Progress Indicator */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Your Progress
          </Typography>
          <Chip
            label={`${onboardingSteps.filter((s) => s.completed).length} of ${onboardingSteps.length} completed`}
            color="primary"
            size="small"
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={
            (onboardingSteps.filter((s) => s.completed).length /
              onboardingSteps.length) *
            100
          }
          sx={{
            height: 10,
            borderRadius: 5,
            bgcolor: `${appTheme.primary}1a`,
            "& .MuiLinearProgress-bar": {
              borderRadius: 5,
              background: `linear-gradient(90deg, ${appTheme.primary} 0%, ${appTheme.secondary} 100%)`,
            },
          }}
        />
      </Paper>

      {/* Steps Grid */}
      <Grid container spacing={3}>
        {onboardingSteps.map((step) => (
          <Grid item xs={12} sm={6} md={3} key={step.step}>
            <Card
            className="owner-dashboard-cards"
              sx={{
                height: "100%",
                position: "relative",
                border: currentStep === step.step ? "2px solid" : "1px solid",
                borderColor:
                  currentStep === step.step ? `${step.color}.main` : "divider",
                // transition: "all 0.3s ease",
                // transition: "none",
                // opacity: step.step > currentStep ? 0.6 : 1,
                // overflow: "visible",
                // "&:hover": {
                //   transform: step.action ? "translateY(-4px)" : "none",
                //   boxShadow: step.action
                //     ? "0 8px 25px rgba(0,0,0,0.15)"
                //     : "none",
                // },
              }}
            >
              {/* Step Badge */}
              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  left: 16,
                  bgcolor: step.completed
                    ? "success.main"
                    : `${step.color}.main`,
                  color: "white",
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: 14,
                }}
              >
                {step.completed ? (
                  <CheckIcon sx={{ fontSize: 18 }} />
                ) : (
                  step.step
                )}
              </Box>

              <CardContent sx={{ pt: 3 }}>
                {/* Icon */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 2,
                    color: step.completed
                      ? "success.main"
                      : `${step.color}.main`,
                  }}
                >
                  {step.icon}
                </Box>

                {/* Title */}
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  textAlign="center"
                  gutterBottom
                  sx={{ fontSize: "1rem" }}
                >
                  {step.title}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ mb: 2, minHeight: 60 }}
                >
                  {step.description}
                </Typography>

                {/* Status / Action */}
                <Box sx={{ textAlign: "center" }}>
                  {step.completed ? (
                    <Chip
                      icon={<CheckIcon />}
                      label="Completed"
                      color="success"
                      size="small"
                    />
                  ) : step.action && currentStep === step.step ? (
                    <Button
                      variant="contained"
                      color={step.color}
                      size="small"
                      onClick={step.action}
                      endIcon={<ArrowIcon />}
                      sx={{ textTransform: "none" }}
                    >
                      {step.actionLabel}
                    </Button>
                  ) : step.step === currentStep ? (
                    <Chip
                      icon={<PendingIcon />}
                      label="In Progress"
                      color="warning"
                      size="small"
                    />
                  ) : (
                    <Chip label="Pending" size="small" variant="outlined" />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* My Properties Section */}
      {safeProperties.length > 0 && (
        <Paper sx={{ p: 3, mt: 4, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              My Properties
            </Typography>
            <Button
              size="small"
              endIcon={<ArrowIcon />}
              onClick={() => navigate("/my-properties")}
            >
              Manage All
            </Button>
          </Box>

          <Grid container spacing={3}>
            {safeProperties.map((property) => {
              const isPending = !property.is_approved;
              const isRented = Number(property.is_rent) === 1;

              return (
                <Grid item xs={12} md={6} key={property._id}>
                  <Box sx={{ position: "relative" }}>
                    {/* Status Banner */}
                    {isPending && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -12,
                          left: "50%",
                          transform: "translateX(-50%)",
                          bgcolor: "warning.main",
                          color: "white",
                          px: 2,
                          py: 0.5,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          zIndex: 1,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        }}
                      >
                        <WaitingIcon sx={{ fontSize: 18 }} />
                        <Typography variant="caption" fontWeight="bold">
                          Awaiting Admin Approval
                        </Typography>
                      </Box>
                    )}

                    <Card
                      sx={{
                        height: "100%",
                        border: isPending ? "2px solid" : "1px solid",
                        borderColor: isPending ? "warning.main" : "divider",
                        // transition: "all 0.3s ease",
                        // "&:hover": {
                        //   transform: "translateY(-4px)",
                        //   boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                        // },
                      }}
                    >
                    <Box sx={{ display: "flex", height: "100%" }}>
                      {/* Property Image */}
                      <Box
                        sx={{
                          width: 140,
                          minHeight: 180,
                          bgcolor: "grey.200",
                          position: "relative",
                          flexShrink: 0,
                        }}
                      >
                        {property.images?.[0] ? (
                          <Box
                            component="img"
                            src={property.images[0]}
                            alt={property.flatName}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              filter: isPending ? "grayscale(30%)" : "none",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <HomeIcon
                              sx={{ fontSize: 48, color: "grey.400" }}
                            />
                          </Box>
                        )}

                        {/* Property Type Badge */}
                        <Chip
                          size="small"
                          label={property.flatType || "Property"}
                          sx={{
                            position: "absolute",
                            bottom: 8,
                            left: 8,
                            bgcolor: "rgba(0,0,0,0.7)",
                            color: "white",
                            fontSize: "0.7rem",
                          }}
                        />
                      </Box>

                      {/* Property Details */}
                      <CardContent sx={{ flex: 1, pt: isPending ? 3 : 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{ fontSize: "1rem" }}
                          >
                            {property.flatName}
                          </Typography>
                          <Chip
                            size="small"
                            icon={
                              isRented ? (
                                <CheckIcon sx={{ fontSize: 14 }} />
                              ) : isPending ? (
                                <PendingIcon sx={{ fontSize: 14 }} />
                              ) : (
                                <Box
                                  component="span"
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    bgcolor: "#4caf50",
                                    ml: 0.5,
                                    animation: "pulse 2s infinite",
                                    "@keyframes pulse": {
                                      "0%": { opacity: 1 },
                                      "50%": { opacity: 0.5 },
                                      "100%": { opacity: 1 },
                                    },
                                  }}
                                />
                              )
                            }
                            label={
                              isRented
                                ? "Rented"
                                : isPending
                                ? "Pending"
                                : "Active"
                            }
                            color={
                              isRented
                                ? "info"
                                : isPending
                                ? "warning"
                                : "success"
                            }
                            sx={{ fontSize: "0.7rem", fontWeight: 600 }}
                          />
                        </Box>

                        {/* Location */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 1.5,
                          }}
                        >
                          <LocationIcon
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                          >
                            {property.flatAddress || "Location not specified"}
                          </Typography>
                        </Box>

                        {/* Property Features */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            mb: 1.5,
                            flexWrap: "wrap",
                          }}
                        >
                          {property.flatBedrooms && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <BedIcon
                                sx={{ fontSize: 16, color: "text.secondary" }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {property.flatBedrooms} Beds
                              </Typography>
                            </Box>
                          )}
                          {property.flatBathrooms && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <BathIcon
                                sx={{ fontSize: 16, color: "text.secondary" }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {property.flatBathrooms} Baths
                              </Typography>
                            </Box>
                          )}
                          {property.flatSize && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <AreaIcon
                                sx={{ fontSize: 16, color: "text.secondary" }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {property.flatSize} sqft
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Rent & Advance Price */}
                        <Box sx={{ mb: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: "0.7rem" }}
                              >
                                Monthly Rent
                              </Typography>
                              <Typography
                                variant="h6"
                                color="primary.main"
                                fontWeight="bold"
                                sx={{ lineHeight: 1.2 }}
                              >
                                PKR{" "}
                                {Number(
                                  property.flatRent || 0
                                ).toLocaleString()}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: "right" }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: "0.7rem" }}
                              >
                                Advance
                              </Typography>
                              <Typography
                                variant="h6"
                                color="success.main"
                                fontWeight="bold"
                                sx={{ lineHeight: 1.2 }}
                              >
                                PKR{" "}
                                {Number(
                                  property.flatAdvance || 0
                                ).toLocaleString()}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => navigate("/my-properties")}
                            sx={{ bgcolor: "grey.100" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Box>

                    {/* Pending Approval Message */}
                    {isPending && (
                      <Box
                        sx={{
                          bgcolor: "warning.main",
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <TimeIcon sx={{ color: "white", fontSize: 20 }} />
                        <Typography variant="caption" sx={{ color: "white" }}>
                          Your property is under review. Admin will approve it
                          within 24 hours.
                        </Typography>
                      </Box>
                    )}
                    </Card>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {/* Add More Properties Button */}
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => navigate("/my-properties")}
            >
              Add Another Property
            </Button>
          </Box>
        </Paper>
      )}

      {/* Help Section */}
      <Paper sx={{ p: 3, mt: 4, bgcolor: "grey.50", borderRadius: 2 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Need Help?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          If you have any questions or need assistance, feel free to contact our
          support team.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate("/my-properties")}
          >
            Add Your First Property
          </Button>
          {safeRequests.length > 0 && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<NotificationIcon />}
              onClick={() => navigate("/new-request")}
            >
              View Requests (
              {safeRequests.filter((r) => r.status === "pending").length})
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );

  if (loading) {
    return (
      <>
        <Wrapper />
        <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
          <div className="mainHeading">
            <h1>Dashboard</h1>
          </div>
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton
                  variant="rectangular"
                  height={160}
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
            ))}
          </Grid>
        </div>
      </>
    );
  }

  // If no tenants, show onboarding view
  if (hasNoTenants) {
    return (
      <>
        <Wrapper />
        <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
          <OnboardingView />
        </div>
      </>
    );
  }

  return (
    <>
      <Wrapper />
      <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
        <div className="mainHeading">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Welcome back,{" "}
                {validateUser?.UserValidate?.data?.user?.ownerName ||
                  "Landlord"}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/my-properties")}
            >
              Add Property
            </Button>
          </Box>
        </div>

        {/* Move-ins waiting on the owner.
            A tenant can pay, move in, and be locked out of bills and
            maintenance simply because the owner never opened the requests
            page. This puts it on the screen they actually land on. */}
        {awaitingConfirmation.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              p: 2.4,
              borderRadius: 2,
              border: "1px solid #fcd34d",
              bgcolor: "#fffbeb",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <HomeIcon sx={{ color: "#b45309" }} />
              <Typography fontWeight={800}>
                {awaitingConfirmation.length === 1
                  ? "A tenant has moved in"
                  : `${awaitingConfirmation.length} tenants have moved in`}
              </Typography>
            </Box>

            {awaitingConfirmation.slice(0, 3).map((request) => (
              <Typography
                key={request._id || request.flatId}
                variant="body2"
                color="text.secondary"
              >
                <strong>{request.tenantName}</strong> says they have moved into{" "}
                <strong>{request.flatName}</strong>.
              </Typography>
            ))}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Confirm it to start the tenancy — until you do, they cannot see
              bills, raise maintenance requests or pay rent.
            </Typography>

            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => navigate("/new-request?tab=2")}
              sx={{ mt: 1.6, textTransform: "none", fontWeight: 700 }}
            >
              Review and confirm
            </Button>
          </Paper>
        )}

        {/* Main Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Properties"
              value={stats.totalProperties}
              subtitle={`${stats.availableProperties} available`}
              icon={<HomeIcon />}
              color="primary"
              onClick={() => navigate("/my-properties")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Tenants"
              value={stats.activeTenants}
              subtitle={`${stats.totalTenants} total`}
              icon={<PeopleIcon />}
              color="success"
              onClick={() => navigate("/my-tenants")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Monthly Earnings"
              value={`${stats.monthlyEarnings.toLocaleString()}`}
              subtitle="PKR from rented properties"
              icon={<MoneyIcon />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Requests"
              value={stats.pendingRequests}
              subtitle="Interest requests"
              icon={<NotificationIcon />}
              color="error"
              onClick={() => navigate("/new-request")}
            />
          </Grid>
        </Grid>

        {/* Secondary Stats & Quick Actions */}
        <Grid container spacing={3}>
          {/* Occupancy Overview */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, height: "100%" }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Property Overview
              </Typography>

              <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* Occupancy Rate */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Occupancy Rate
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {occupancyRate}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={occupancyRate}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: `${appTheme.primary}1a`,
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 5,
                          bgcolor:
                            occupancyRate > 70
                              ? "success.main"
                              : occupancyRate > 40
                              ? "warning.main"
                              : "error.main",
                        },
                      }}
                    />
                  </Box>

                  {/* Property Status Breakdown */}
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Chip
                      icon={<CheckIcon />}
                      label={`${stats.rentedProperties} Rented`}
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      icon={<HomeIcon />}
                      label={`${stats.availableProperties} Available`}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      icon={<PendingIcon />}
                      label={`${stats.pendingApproval} Pending`}
                      color="warning"
                      variant="outlined"
                    />
                  </Box>
                </Grid>

                {/* Earnings Summary */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "success.light",
                      borderRadius: 2,
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" color="success.dark">
                      Current Monthly Income
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      color="success.dark"
                    >
                      PKR {stats.monthlyEarnings.toLocaleString()}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "grey.100",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Potential Monthly (If 100% Occupied)
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      PKR {stats.expectedEarnings.toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: "100%" }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Quick Actions
              </Typography>

              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
              >
                <QuickAction
                  icon={<AddIcon />}
                  label="Add New Property"
                  onClick={() => navigate("/my-properties")}
                />
                <QuickAction
                  icon={<PeopleIcon />}
                  label="View Tenants"
                  onClick={() => navigate("/my-tenants")}
                  color="success"
                />
                <QuickAction
                  icon={<NotificationIcon />}
                  label={`View Requests (${stats.pendingRequests})`}
                  onClick={() => navigate("/new-request")}
                  color="warning"
                />
                <QuickAction
                  icon={<MoneyIcon />}
                  label="Rent Tracker"
                  onClick={() => navigate("/rent-tracker")}
                  color="info"
                />
                <QuickAction
                  icon={<MaintenanceIcon />}
                  label="Maintenance Requests"
                  onClick={() => navigate("/landlord-maintenance-request")}
                  color="secondary"
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Recent Properties & Requests */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Recent Properties */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  Recent Properties
                </Typography>
                <Button
                  size="small"
                  endIcon={<ArrowIcon />}
                  onClick={() => navigate("/my-properties")}
                >
                  View All
                </Button>
              </Box>

              {safeProperties.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <HomeIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
                  <Typography color="text.secondary">
                    No properties added yet
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{ mt: 2 }}
                    onClick={() => navigate("/my-properties")}
                  >
                    Add Your First Property
                  </Button>
                </Box>
              ) : (
                <List>
                  {safeProperties.slice(0, 4).map((property, index) => (
                    <React.Fragment key={property._id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar
                            src={property.images?.[0]}
                            variant="rounded"
                            sx={{ width: 56, height: 56 }}
                          >
                            <HomeIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={property.flatName}
                          secondary={`PKR ${property.flatRent?.toLocaleString()} / month`}
                          sx={{ ml: 1 }}
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            size="small"
                            label={
                              Number(property.is_rent) === 1
                                ? "Rented"
                                : property.is_approved
                                ? "Available"
                                : "Pending"
                            }
                            color={
                              Number(property.is_rent) === 1
                                ? "success"
                                : property.is_approved
                                ? "primary"
                                : "warning"
                            }
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < Math.min(safeProperties.length, 4) - 1 && (
                        <Divider />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Recent Interest Requests */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  Recent Requests
                </Typography>
                <Button
                  size="small"
                  endIcon={<ArrowIcon />}
                  onClick={() => navigate("/new-request")}
                >
                  View All
                </Button>
              </Box>

              {safeRequests.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <NotificationIcon
                    sx={{ fontSize: 48, color: "grey.400", mb: 1 }}
                  />
                  <Typography color="text.secondary">
                    No requests yet
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Tenant requests will appear here
                  </Typography>
                </Box>
              ) : (
                <List>
                  {safeRequests.slice(0, 4).map((request, index) => (
                    <React.Fragment key={request._id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: "primary.light" }}>
                            <PeopleIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={request.tenantName || "Tenant Request"}
                          secondary={`For: ${request.flatName || "Property"}`}
                          sx={{ ml: 1 }}
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            size="small"
                            label={request.status}
                            color={
                              request.status === "pending"
                                ? "warning"
                                : request.status === "accepted"
                                ? "success"
                                : "error"
                            }
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < Math.min(safeRequests.length, 4) - 1 && (
                        <Divider />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      </div>
    </>
  );
}
